"use client";

import { useEffect, useId, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/Button";
import Magnetic from "@/components/ui/Magnetic";
import Heading from "@/components/ui/Heading";
import { cn } from "@/utils";
import { useAfterTransition } from "@/hooks/useAfterTransition";
import AltchaLoadBoundary from "./AltchaLoadBoundary";

// Client-only loader that registers the <altcha-widget> custom element. A bare
// `import("altcha")` in an effect / with a discarded namespace was NOT reliably
// instantiated by Turbopack (the element never upgraded → the hidden `altcha`
// payload stayed empty → every submit 400'd). AltchaScript uses a static
// side-effect `import "altcha"` that the bundler preserves; ssr:false keeps it
// client-only (SSR has no customElements).
const AltchaScript = dynamic(() => import("./AltchaScript"), {
  ssr: false
});

// R3F success FX (its own tiny canvas). MUST be dynamic + ssr:false: ContactForm
// is imported app-wide via ContactModal, and statically importing three/R3F into
// that SSR'd tree breaks client hydration everywhere. Lazy-loads on success only.
const ContactSuccessFX = dynamic(() => import("@/components/Contact/ContactSuccessFX"), {
  ssr: false
});
type Status = "idle" | "submitting" | "success" | "error";
type Verification = "pending" | "ready" | "error";
type AltchaElement = HTMLElement & {
  getState?: () => string;
};
type Fields = {
  name: string;
  email: string;
  subject: string;
  message: string;
};
const EMPTY: Fields = {
  name: "",
  email: "",
  subject: "",
  message: ""
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const labelClass = "font-secondary text-xs uppercase tracking-widest text-primary/70 text-left";
const fieldClass = "w-full rounded-lg border border-white/15 bg-dark/60 px-4 py-3 font-secondary text-primary placeholder:text-primary/50 outline-none transition-colors focus:border-accent focus:ring-1 focus:ring-accent";
const errorClass = "font-secondary text-xs text-accent";
const ContactForm = ({
  variant = "page",
  onSuccess
}: {
  variant?: "modal" | "page";
  onSuccess?: () => void;
}) => {
  const id = useId();
  const t = useTranslations("contact.form");
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [validationFailed, setValidationFailed] = useState(false);
  const successHeadingRef = useRef<HTMLDivElement>(null);
  const altchaRef = useRef<AltchaElement | null>(null);
  const settled = useAfterTransition();
  const [verification, setVerification] = useState<Verification>("pending");
  const [verificationRequested, setVerificationRequested] = useState(false);
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const succeeded = status === "success";
  useEffect(() => {
    const widget = altchaRef.current;
    if (!settled || succeeded || !widget) return;
    // Covers a stalled challenge/registration as well as explicit widget errors.
    // A late successful solve can still recover; a timeout never permits a POST.
    const timeout = window.setTimeout(() => setVerification("error"), 30000);
    const sync = () => {
      const state = widget.getState?.();
      if (state === "verified") {
        clearTimeout(timeout);
        setVerification("ready");
        setVerificationRequested(false);
      } else if (state === "error" || state === "expired") {
        clearTimeout(timeout);
        setVerification("error");
      } else setVerification("pending");
    };
    widget.addEventListener("statechange", sync);
    sync();
    return () => {
      clearTimeout(timeout);
      widget.removeEventListener("statechange", sync);
    };
  }, [settled, succeeded, verificationAttempt]);
  useEffect(() => {
    if (status === "success") successHeadingRef.current?.focus();
  }, [status]);
  const update = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFields(f => ({
    ...f,
    [k]: e.target.value
  }));

  // ponytail: returns errors map so handleSubmit can focus the first field without re-deriving conditions
  const validate = (): Partial<Record<keyof Fields, string>> => {
    const next: Partial<Record<keyof Fields, string>> = {};
    if (!fields.name.trim()) next.name = t("validation.required");
    if (!fields.email.trim()) next.email = t("validation.required");else if (!EMAIL_RE.test(fields.email.trim())) next.email = t("validation.email");
    if (!fields.subject.trim()) next.subject = t("validation.required");
    if (!fields.message.trim()) next.message = t("validation.required");else if (fields.message.trim().length < 10) next.message = t("validation.messageShort");
    setErrors(next);
    return next;
  };
  const FIELD_ORDER: (keyof Fields)[] = ["name", "email", "subject", "message"];
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (status === "submitting") return;
    setValidationFailed(false);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setValidationFailed(true);
      const first = FIELD_ORDER.find(k_0 => k_0 in errs);
      if (first) document.getElementById(`${id}-${first}`)?.focus();
      return;
    }
    // Read the live widget, not cached state: proofs can expire after a solve.
    const widget_0 = altchaRef.current;
    const payload = widget_0?.querySelector<HTMLInputElement>('input[name="altcha"]')?.value;
    if (widget_0?.getState?.() !== "verified" || !payload) {
      setVerificationRequested(true);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...fields,
          company,
          altcha: payload
        })
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
      };
      if (res.ok && data.ok) {
        setStatus("success");
        setFields(EMPTY);
        onSuccess?.();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };
  const statusText = status === "success" ? t("successTitle") : status === "error" ? t("errorGeneric") : validationFailed ? t("validation.formError") : registrationFailed ? t("verificationUnavailable") : verification === "error" ? t("verificationError") : verificationRequested && verification !== "ready" ? t("verificationPending") : "";

  // ponytail: single persistent live region; pre-exists before any state change so AT reliably announces content switches
  return <div>
      {/* sr-only node is always mounted so AT registers it before content changes */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {statusText}
      </div>
      {status === "success" ? <div className="relative flex flex-col items-center justify-center text-center ~gap-3/4 ~p-6/10">
          <ContactSuccessFX />
          {/* Heading doesn't forward refs → wrap for focus-on-success + a11y. */}
          <div ref={successHeadingRef} tabIndex={-1} className="relative outline-none">
            <Heading level={4} fx className="text-mint">
              {t("successTitle")}
            </Heading>
          </div>
          <p className="relative font-secondary text-primary/80 ~text-sm/base">
            {t("successBody")}
          </p>
        </div> : <form noValidate onSubmit={handleSubmit} className={cn("relative flex flex-col ~gap-4/5", variant === "page" && "max-w-xl")}>
          {/* Honeypot — visually hidden, off the tab order, ignored by AT. */}
          {/* ponytail: name="winnie" is non-semantic intentionally — avoids browser autofill on "company"/"organization" heuristics */}
          <input type="text" name="winnie" value={company} onChange={e_1 => setCompany(e_1.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] size-0 opacity-0" />

          {/* ALTCHA proof-of-work — invisible; solves on load, payload read at
              submit from the widget's hidden input[name="altcha"]. AltchaScript
              registers the custom element client-side. */}
          {/* Gate the element too: registration persists across route visits. */}
          {settled && <>
              <AltchaLoadBoundary onError={() => setRegistrationFailed(true)}>
                <AltchaScript />
              </AltchaLoadBoundary>
              <altcha-widget key={verificationAttempt} ref={altchaRef} challenge="/api/contact/challenge" auto="onload" style={{
          display: "none"
        }} />
            </>}

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-name`} className={labelClass}>
              {t("name")}
            </label>
            <input id={`${id}-name`} type="text" name="name" autoComplete="name" value={fields.name} onChange={update("name")} placeholder={t("namePlaceholder")} className={fieldClass} aria-required="true" aria-invalid={errors.name ? true : undefined} aria-describedby={errors.name ? `${id}-name-err` : undefined} />
            {errors.name && <span id={`${id}-name-err`} className={errorClass}>
                {errors.name}
              </span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-email`} className={labelClass}>
              {t("email")}
            </label>
            <input id={`${id}-email`} type="email" name="email" autoComplete="email" value={fields.email} onChange={update("email")} placeholder={t("emailPlaceholder")} className={fieldClass} aria-required="true" aria-invalid={errors.email ? true : undefined} aria-describedby={errors.email ? `${id}-email-err` : undefined} />
            {errors.email && <span id={`${id}-email-err`} className={errorClass}>
                {errors.email}
              </span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-subject`} className={labelClass}>
              {t("subject")}
            </label>
            <input id={`${id}-subject`} type="text" name="subject" value={fields.subject} onChange={update("subject")} placeholder={t("subjectPlaceholder")} className={fieldClass} aria-required="true" aria-invalid={errors.subject ? true : undefined} aria-describedby={errors.subject ? `${id}-subject-err` : undefined} />
            {errors.subject && <span id={`${id}-subject-err`} className={errorClass}>
                {errors.subject}
              </span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-message`} className={labelClass}>
              {t("message")}
            </label>
            <textarea id={`${id}-message`} name="message" rows={5} value={fields.message} onChange={update("message")} placeholder={t("messagePlaceholder")} className={cn(fieldClass, "resize-none")} aria-required="true" aria-invalid={errors.message ? true : undefined} aria-describedby={errors.message ? `${id}-message-err` : undefined} />
            {errors.message && <span id={`${id}-message-err`} className={errorClass}>
                {errors.message}
              </span>}
          </div>

          {(registrationFailed || verification === "error" || verificationRequested && verification !== "ready") && <div data-contact-verification={registrationFailed ? "unavailable" : verification} className="font-secondary text-sm text-accent">
              <p>
                {t(registrationFailed ? "verificationUnavailable" : verification === "error" ? "verificationError" : "verificationPending")}
              </p>
              {!registrationFailed && verification === "error" && <button type="button" className="mt-2 underline underline-offset-4" onClick={() => {
          setVerification("pending");
          setVerificationRequested(true);
          setVerificationAttempt(attempt => attempt + 1);
        }}>
                  {t("verificationRetry")}
                </button>}
            </div>}

          {status === "error" && <p className="font-secondary text-sm text-accent">
              {t("errorGeneric")}
            </p>}

          <Magnetic className="mt-2 self-start">
            <Button variant="primary" type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? t("submitting") : t("submit")}
            </Button>
          </Magnetic>
        </form>}
    </div>;
};
export default ContactForm;